import React, { useMemo, useState } from 'react';
import { Button, FeaturedIcon } from '@myhealth-raccoon/components';
import { Navigate, useNavigate } from 'react-router-dom';

import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { useDispatch, useSelector } from 'src/store';
import { PaymentEmbed } from 'src/components/PaymentEmbed';
import { useAsyncData } from '@myhealth-raccoon/services';
import { patientService } from 'src/services/patient';
import { ContentContainer } from 'src/components/ContentContainer';
import { StickyBottomBar } from 'src/components/StickyBottomBar';
import { useAppointmentTypeSelector } from 'src/hooks/useAsyncSelector';
import { PleaseWait } from 'src/components/PleaseWait';
import { setPaymentStatus, setSawPrepaymentMessage } from 'src/store/bookingFlow';
import { setPaymentId } from 'src/store/appointment';
import { useBeforeUnload } from 'src/hooks/useBeforeUnload';

export function Payment({ nextPage, retry }: {
    nextPage: string;
    retry: string;
}) {
    useBookingFlowStep(2);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // show dialog before closing/reloading
    useBeforeUnload();

    const { data: appointmentType } = useAppointmentTypeSelector();
    const [paymentStatus] = useState(useSelector(state => state.bookingFlow.paymentStatus));
    const siteId = useSelector(state => state.appointment.clinicId);
    const [error, setError] = useState<string>();

    const { data: basicDetails } = useAsyncData({
        method: patientService.GetBasicDetails,
        service: patientService
    }, siteId);

    const { paymentMethod, paymentRequired } = useMemo(() => {
        if (!appointmentType) {
            return { paymentMethod: undefined, paymentRequired: undefined };
        }
        if (appointmentType.prePaymentRequired && Number(appointmentType.prePaymentFees) > 0) {
            return { paymentMethod: 2, paymentRequired: true }
        } else if (appointmentType.postPaymentRequired && Number(appointmentType.postBillingFees) > 0) {
            return { paymentMethod: 3, paymentRequired: true }
        } else {
            return { paymentMethod: undefined, paymentRequired: false }
        }
    }, [appointmentType])

    if (paymentRequired === false || paymentStatus === "COMPLETED") {
        return <Navigate replace to={nextPage} />
    }

    if (paymentRequired === undefined) {
        return <ContentContainer>
            <PleaseWait />
        </ContentContainer>
    }

    if (paymentStatus === "STARTED") {
        // restart
        dispatch(setSawPrepaymentMessage(true));
        navigate(retry, { replace: true })
        return null;
    }

    return <>
        {error && <>
            <ContentContainer className="text-center flex-1 lg:flex-none">
                <FeaturedIcon className='mx-auto mb-2.5' />
                <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Payment Error</h1>
            </ContentContainer>
            <StickyBottomBar>
                <Button
                    className='flex-1 text-center flex items-center justify-center gap-2'
                    onClick={() => {
                        dispatch(setSawPrepaymentMessage(true));
                        navigate(retry, { replace: true })
                    }}
                >
                    Retry
                </Button>
            </StickyBottomBar>
        </>}

        {!error && paymentMethod && basicDetails &&
            <>
                <ContentContainer className='flex-1 lg:flex-none'>
                    <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Payment</h1>
                    <PaymentEmbed
                        onError={setError}
                        paymentMethod={paymentMethod}
                        onConfirm={(payment_id) => {
                            dispatch(setPaymentId(Number(payment_id)));
                            dispatch(setPaymentStatus('COMPLETED'));
                            navigate(nextPage);
                        }}
                        patientName={`${basicDetails.firstname} ${basicDetails.surname}`}
                    />
                </ContentContainer>
                <StickyBottomBar>
                    <Button
                        onClick={() => navigate(-1)}
                        className='flex-1 text-center' color='gray'>Back</Button>
                </StickyBottomBar>
            </>}
    </>
}